import * as React from "react";
import { gql, useQuery } from "@apollo/client";
import {
  ViewChildOffersQueryQueryVariables,
  ViewChildOffersQueryQuery
} from "../../../schemaTypes";

export const viewChildOffersQuery = gql`
  query ViewChildOffersQuery(
    $userId: String!
    $offset: Int!
    $limit: Int!
  ) {
    childOffers(
      userId: $userId
      offset: $offset, 
      limit: $limit
    ) {
      id
      title
      description
      bannerUrl
      # category {
      #   id
      #   name
      #   imgSrc
      #   createdAt
      #   # updatedAt
      # }
      offerType
      status
      # discountType
      # discountValue
      # partnerVoucher
      # referralUrl
      offerTOR
      startDate
      endDate
      # community{
      #   id
      #   name
      #   alias
      # }
      # location{
      #   id
      #   city
      # }
      createdBy{
        id
        admin {
          id
          merchantProfile {
            id
            # address
            # city
            # country
            # postcode
            companyName
            username
            # industry {
            #   id
            #   name
            # }
            companyLocation
            logoUrl
            bannerUrl
            description
          }
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const newChildOfferSubscription = gql`
  subscription NewChildOfferSubscription($userId: String!) {
    newChildOffer(userId: $userId) {
      id
      title
      description
      bannerUrl
      category {
        id
        name
        imgSrc
        createdBy{
          id
          role
        }
        createdAt
        updatedAt
      }
      offerType
      discountType
      discountValue
      partnerVoucher
      referralUrl
      status
      usageLimitPerUser
      totalUsageLimit
      startDate
      endDate
      community{
        id
        name
        alias
      }
      location{
        id
        city
        district
        streetName
        country
        postalCode
        landmark
        latitude
        longitude
        createdAt
      }
      redemptions
      revenueGenerated
      clickThroughs
      featured
      createdBy{
        id
        role
        admin {
          id
          shortId
          firstname
          lastname
          email
          phone
          merchantProfile {
            id
            businessName
            businessType
            regCountry
            businessRegNumber
            taxId
            address
            addressAlt
            city
            country
            postcode
            companyName
            username
            industry {
              id
              name
              createdBy{
                id
                role
              }
              createdAt
              updatedAt
            }
            companyLocation
            logoUrl
            bannerUrl
            description
            commissionRate
            createdAt
            updatedAt
          }
          createdAt
          updatedAt
        }
      }
      # njoyCode {
      #   id
      #   code
      #   redeemedAt
      #   createdAt
      #   updatedAt
      # }
      createdAt
      updatedAt
    }
  }
`;

export function useViewChildOffers(variables: ViewChildOffersQueryQueryVariables) {
  const { data, loading, fetchMore, subscribeToMore } = useQuery<ViewChildOffersQueryQuery, ViewChildOffersQueryQueryVariables>(
    viewChildOffersQuery,
    { variables }
  );

  React.useEffect(() => {
    if (!subscribeToMore) return;
    const unsubscribe = subscribeToMore({
      document: newChildOfferSubscription,
      variables: { userId: variables.userId },
      updateQuery: (prev, { subscriptionData }) => {
        if (!subscriptionData.data) return prev;
        return {
          ...prev,
          childOffers: [
            ...prev.childOffers,
            (subscriptionData.data as any).newChildOffer
          ]
        };
      }
    });
    return () => unsubscribe();
  }, [subscribeToMore, variables.userId]);

  // Use the generated type for each offer item
  type ChildOffer = ViewChildOffersQueryQuery['childOffers'][number];
  const childOffers: ChildOffer[] = data?.childOffers ?? [];
  let hasMoreChildOffers = childOffers.length % variables.limit === 0;
  if (childOffers.length <= variables.offset) {
    hasMoreChildOffers = false;
  }

  const loadMore = () => {
    fetchMore({
      variables: {
        ...variables,
        offset: childOffers.length
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        return {
          ...prev,
          childOffers: [
            ...prev.childOffers,
            ...fetchMoreResult.childOffers
          ]
        };
      }
    });
  };

  return {
    childOffers,
    loading,
    loadMore,
    hasMoreChildOffers
  };
}